/**
 * `numbers` — right-aligned, unit and precision from settings (M2 §5.5, §6).
 * The editor rejects non-numeric characters; a pasted `1,234.5` / `1.234,5` is stripped of its
 * thousands separators before parsing (ASSUMED, M2 §11 Q63).
 */
import { formatNumbers, getColumnType, type NumbersSettings } from '@elizmonday/shared';
import { CellText, InlineTextEditor } from '../primitives';
import { defineColumnUi } from '../types';
import { sharedBuckets } from '../buckets';
import { NumbersSettingsDialog } from './SettingsDialog';

const shared = getColumnType('numbers');

/** `1,234.5` -> `1234.5`, `1.234,5` -> `1234.5`, `$ 12` -> `12` (ASSUMED, Q63). */
export function stripThousands(input: string): string {
  const trimmed = input.trim().replace(/[^\d.,+-]/g, '');
  if (trimmed === '') return '';
  const lastComma = trimmed.lastIndexOf(',');
  const lastDot = trimmed.lastIndexOf('.');
  if (lastComma > lastDot) return trimmed.replaceAll('.', '').replace(',', '.');
  return trimmed.replaceAll(',', '');
}

/**
 * What may be **in progress** in the input, which is wider than what may be committed: `-`,
 * `1.`, `1,` and `` are all legal intermediate states of typing a number, and a separator is
 * allowed because `stripThousands` handles both conventions on commit (Q63).
 */
export function acceptsWhileTyping(draft: string): boolean {
  return /^[+-]?[\d.,]*$/.test(draft);
}

export const numbersColumnUi = defineColumnUi<string, NumbersSettings>({
  type: 'numbers',
  icon: 'columnNumbers',
  gallery: { group: 'numbers', keywords: ['number', 'numbers', 'price', 'amount', 'quantity'] },
  /** P-022: the registry default. ASSUMED — no `width: null` column of this type is measured. */
  defaultWidth: 140,
  align: 'right',
  groupable: true,
  /** P-263: the value is item-independent, so it may be written to a whole selection. */
  massEditable: true,

  /*
   * **`formatNumbers`, not `toText`.** `toText` is the API's string and is documented as never
   * applying the unit, the separator or the precision (`packages/shared/src/columns/numbers`:
   * *"`formatNumbers()` does that, for the cell and the footer only"*) — monday's own `text` for
   * a `unit.precision: 2` column is the stored `"3.00"`, not a re-rounded one. The **cell** is the
   * other half of that sentence, and it was calling `toText`: a column set to `€`, `left` and two
   * decimals painted a bare `42`. Found by round 10i's settings verifier, which changes the unit
   * and then asserts the grid repaints (`S3`).
   */
  Cell: ({ value, settings }) => <CellText text={formatNumbers(value, settings)} align="right" />,

  /**
   * Rebuilt from `discovery/ui-reference/editors/numbers/inline-behaviour.json`: the input
   * carries the **raw** number (`190.5`, never the rendered `$190.50`), **the whole value is
   * selected** when it opens (`selectionStart: 0, selectionEnd: 5` — you retype a number, you
   * do not append to it), and monday puts an `×` inside the editing cell that clears it. The
   * README's "non-numeric input is rejected as it is typed" is the `accept` predicate: a
   * keystroke that could not become a number never enters the draft.
   */
  Editor: ({ value, initialInput, autoFocus, onCommit, onCancel, onNavigate }) => (
    <InlineTextEditor
      initial={value ?? ''}
      initialInput={initialInput}
      autoFocus={autoFocus}
      align="right"
      inputMode="decimal"
      caret="all"
      clearable
      accept={acceptsWhileTyping}
      ariaLabel="Number"
      onCommit={(next) => {
        const cleaned = stripThousands(next);
        if (cleaned === '') return onCommit(null);
        try {
          onCommit(shared.parseValue(cleaned, {}) as string | null);
        } catch {
          onCancel();
        }
      }}
      onCancel={onCancel}
      onNavigate={onNavigate}
    />
  ),

  SettingsDialog: NumbersSettingsDialog,

  fromClipboard(text, settings) {
    const cleaned = stripThousands(text);
    return cleaned === '' ? null : (shared.parseValue(cleaned, settings) as string | null);
  },
  toClipboard(value, settings) {
    return shared.toText(value, settings) ?? '';
  },
  bucketsOf: sharedBuckets('numbers'),
});
